import { Languages } from 'lucide-react';

import { En } from '../i18n/En';
import { useLocale } from '../i18n/LocaleProvider';
import { LOCALES, SHIPPED_LOCALES, type ShippedLocale } from '../i18n/locale';
import { t, translate } from '../i18n/t';
import { Select, type SelectOption } from './Select';

/**
 * The heading, with the English beside it on a page that is not English, so a
 * reader who cannot read the language they were put in still finds the way
 * out. The icon is the other half of that.
 */
function Heading() {
  const { locale } = useLocale();
  const english = locale === 'en' || locale === 'qps';
  return (
    <span className="flex items-center gap-2">
      <Languages aria-hidden className="h-5 w-5 shrink-0" />
      <span>{t('language-title')}</span>
      {!english && (
        <En>
          <span className="text-base-content/60 font-normal">{translate('en', 'language-title')}</span>
        </En>
      )}
    </span>
  );
}

/**
 * Every shipped locale, each named in its own language and script, never
 * translated (spec §3.2, as amended for phase 2a's picker).
 */
const OPTIONS: readonly SelectOption[] = SHIPPED_LOCALES.map((code) => ({
  value: code,
  label: LOCALES[code].endonym,
  lang: code,
  dir: LOCALES[code].dir,
}));

/** Applied on choosing; `disabled` while the choice is being stored. */
export function LanguagePicker({
  value,
  onChange,
  disabled,
  error,
}: {
  value: ShippedLocale;
  onChange: (locale: ShippedLocale) => void;
  disabled?: boolean;
  error?: string | null;
}) {
  return (
    <fieldset className="fieldset min-w-0">
      <label className="fieldset-legend pt-0 text-base" htmlFor="display-language">
        <Heading />
      </label>
      <p className="text-base-content/70 text-sm" id="display-language-intro">
        {t('language-intro')}
      </p>
      <Select
        id="display-language"
        value={value}
        options={OPTIONS}
        onChange={(v) => onChange(v as ShippedLocale)}
        disabled={disabled}
        aria-describedby="display-language-intro"
      />
      {error && (
        <p className="text-error text-sm" role="alert">
          {error}
        </p>
      )}
    </fieldset>
  );
}

/**
 * The same choice before signing in, in the corner of the page: nothing to
 * store it on but this browser, and a form under it that must stay the
 * page's subject.
 */
export function CompactLanguagePicker() {
  const { locale, choose } = useLocale();
  return (
    <div className="flex items-center justify-end gap-1 text-sm">
      <Languages aria-hidden className="h-4 w-4" />
      <Select
        value={locale === 'qps' ? 'en' : locale}
        options={OPTIONS}
        onChange={(v) => void choose(v as ShippedLocale)}
        aria-label={t('language-title')}
        ghost
      />
    </div>
  );
}
